/**
 * MessageContent - Renders markdown content with syntax highlighting
 * 
 * Features:
 * - Markdown rendering with react-markdown
 * - Syntax highlighting for code blocks
 * - Support for tables, lists, links, images
 * - Streaming text animation
 * - Styling consistent with design system
 */

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import { Link } from "react-router";
import { Copy01, CheckCircle } from "@untitledui/icons";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { UIResourceRenderer, type UIResource } from "./ui-resource-renderer";
import { useSetting } from "@/hooks/api/use-settings";

// Note: highlight.js styles should be imported globally or via CSS
// For now, we'll use inline styles for code blocks

/** Remove JSON blocks that contain SQL, tool_result, or query results (security). */
function sanitizeMessageContent(text: string): string {
    if (!text || text.length === 0) return text;
    const sensitiveMarkers = [/"sql"\s*:/i, /"tool_result"/i, /"tables_used"\s*:/i];
    const findFirst = (str: string, from: number): number | null => {
        let min: number | null = null;
        for (const re of sensitiveMarkers) {
            const m = str.slice(from).match(re);
            if (m && m.index !== undefined) {
                const pos = from + m.index;
                if (min === null || pos < min) min = pos;
            }
        }
        return min;
    };
    const placeholder = "\n[Dati di query non mostrati per sicurezza.]\n";
    let out = text;
    let start = 0;
    for (;;) {
        const next = findFirst(out, start);
        if (next === null) break;
        let open = next;
        while (open > 0 && out[open - 1] !== "{") open--;
        if (open === 0 || out[open - 1] !== "{") {
            start = next + 1;
            continue;
        }
        open -= 1;
        let depth = 1;
        let close = open + 1;
        while (close < out.length && depth > 0) {
            if (out[close] === "{") depth++;
            else if (out[close] === "}") depth--;
            close++;
        }
        out = out.slice(0, open) + placeholder + out.slice(close);
        start = open + placeholder.length;
    }
    return out.replace(/(\n\[Dati di query non mostrati per sicurezza\.\]\n)+/g, placeholder.trim());
}

interface MessageContentProps {
    content: string;
    uiResources?: UIResource[];
}

export const MessageContent = ({ content, uiResources = [] }: MessageContentProps) => {
    const { t } = useTranslation(['common']);
    const [copied, setCopied] = useState(false);

    // Fetch setting for enabling/disabling MCP UI
    // If user doesn't have permission to read settings, default to enabled (permissive)
    const { data: mcpUiSetting, error: mcpUiSettingError } = useSetting("eve_care_chat", "enable_mcp_ui", "global");
    const mcpUiEnabled = mcpUiSettingError
        ? true // Default to enabled if permission denied or error
        : (mcpUiSetting?.value !== false); // Default to true if setting not loaded yet

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopied(true);
            toast.success(t('aiChat.actions.copied', { defaultValue: "Copied to clipboard" }));
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error(t('aiChat.actions.copyFailed', { defaultValue: "Failed to copy" }));
        }
    };

    return (
        <div className="relative group">
            {/* Render UI resources first (e.g., maps) - only if MCP UI is enabled */}
            {uiResources.length > 0 && mcpUiEnabled && (
                <div className="mb-4 space-y-4">
                    {uiResources.map((resource, index) => (
                        <UIResourceRenderer
                            key={resource.uri || index}
                            resource={resource}
                            className="w-full"
                        />
                    ))}
                </div>
            )}

            {/* Render markdown content (sanitized: no SQL/tool_result JSON) */}
            <div className="prose prose-sm dark:prose-invert max-w-none [&_*]:text-primary [&_a]:text-brand [&_code]:bg-secondary [&_pre]:bg-secondary/50 pb-8">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight, rehypeRaw]}
                    components={{
                        code: ({ node, inline, className, children, ...props }: any) => {
                            const match = /language-(\w+)/.exec(className || "");
                            const language = match ? match[1] : "";
                            const codeString = String(children).replace(/\n$/, "");

                            if (inline) {
                                return (
                                    <code className="px-1.5 py-0.5 rounded bg-secondary text-sm font-mono border border-secondary" {...props}>
                                        {children}
                                    </code>
                                );
                            }

                            return (
                                <div className="relative my-4">
                                    {language && (
                                        <div className="absolute top-2 right-2 text-xs text-tertiary">
                                            {language}
                                        </div>
                                    )}
                                    <pre className="rounded-lg bg-secondary/50 border border-secondary p-4 overflow-x-auto text-xs">
                                        <code className={className} {...props} style={{ fontFamily: 'monospace' }}>
                                            {codeString}
                                        </code>
                                    </pre>
                                </div>
                            );
                        },
                        table: ({ children }: any) => (
                            <div className="overflow-x-auto my-4">
                                <table className="min-w-full border-collapse border border-secondary">
                                    {children}
                                </table>
                            </div>
                        ),
                        th: ({ children }: any) => (
                            <th className="border border-secondary bg-secondary/50 px-4 py-2 text-left font-semibold">
                                {children}
                            </th>
                        ),
                        td: ({ children }: any) => (
                            <td className="border border-secondary px-4 py-2">
                                {children}
                            </td>
                        ),
                        a: ({ href, children }: any) => {
                            const containerPath = (() => {
                                if (!href || typeof href !== "string") return null;
                                if (href.startsWith("/shipper/containers/")) return href;
                                // Match /containers/<uuid> in any path or full URL (e.g. https://app.eve-mind.com/containers/UUID)
                                const uuidMatch = href.match(/\/containers\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
                                return uuidMatch ? `/shipper/containers/${uuidMatch[1]}` : null;
                            })();
                            if (containerPath) {
                                return (
                                    <Link
                                        to={containerPath}
                                        className="text-brand hover:underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {children}
                                    </Link>
                                );
                            }
                            return (
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-brand hover:underline"
                                >
                                    {children}
                                </a>
                            );
                        },
                        p: ({ children }: any) => (
                            <p className="mb-3 last:mb-0">{children}</p>
                        ),
                        ul: ({ children }: any) => (
                            <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>
                        ),
                        ol: ({ children }: any) => (
                            <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>
                        ),
                        li: ({ children }: any) => (
                            <li className="ml-4">{children}</li>
                        ),
                    }}
                >
                    {sanitizeMessageContent(content)}
                </ReactMarkdown>
            </div>
            {/* Copy button - inside bubble, below text to avoid overlap */}
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    onClick={handleCopy}
                    className="p-1.5 rounded-md hover:bg-secondary text-tertiary hover:text-primary"
                    aria-label="Copy message"
                >
                    {copied ? (
                        <CheckCircle className="size-4 text-green-500" />
                    ) : (
                        <Copy01 className="size-4" />
                    )}
                </button>
            </div>
        </div>
    );
};

